import { useEffect, useRef } from "react";
import { ProvetDialogStep } from "../types/dialogSteps";

/** Input types the customer doesn't type into, such as the clinic and treatment radios. */
const NON_TEXT_INPUT_TYPES = [
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit"
];

/**
 * Whether the element is a field the customer types into or picks a value in.
 */
const isEditableField = (element: Element | null): element is HTMLElement => {
  if (element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
    return true;
  }

  if (element instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.includes(element.type);
  }

  return element instanceof HTMLElement && element.isContentEditable;
};

/**
 * Moves focus to a field programmatically, unless the customer is already in a field of the
 * same form: moving focus then would send the rest of their typing into the wrong field
 * (observation 24). Also skips fields that can't take focus yet, e.g. in a closed (inert)
 * step.
 *
 * @returns Whether focus was moved.
 */
export const focusUnlessCustomerIsTyping = (target: HTMLElement | null): boolean => {
  if (!target || !target.isConnected || target.closest("[inert]")) {
    return false;
  }

  const active = document.activeElement;
  const scope = target.closest("form") ?? document.body;

  if (active !== target && isEditableField(active) && scope.contains(active)) {
    return false;
  }

  // Mark as programmatically focused for custom styling
  target.setAttribute("data-programmatic-focus", "true");

  // Use preventScroll to avoid any interference from scrolling
  target.focus({ preventScroll: true });

  // Remove the marker when user interacts (blur or any key press)
  const removeMarker = () => {
    target.removeAttribute("data-programmatic-focus");
    target.removeEventListener("blur", removeMarker);
    target.removeEventListener("keydown", removeMarker);
  };

  target.addEventListener("blur", removeMarker, { once: true });
  target.addEventListener("keydown", removeMarker, { once: true });

  return document.activeElement === target;
};

/**
 * Focuses the first field of the contact details form (the identification number). Called by
 * the form when it's shown and its step is open, e.g. right after the phone verification.
 */
export const focusContactDetailsForm = (): boolean =>
  focusUnlessCustomerIsTyping(
    document.querySelector<HTMLElement>("#identification-number")
  );

interface UseProvetFocusManagementOptions {
  sessionId: string;
  activeStep?: ProvetDialogStep;
}

/**
 * Custom hook to manage focus behavior for the Provet booking form.
 * Automatically focuses the first main input/element when a new step becomes active.
 * This improves keyboard accessibility without affecting the existing scroll behavior.
 */
export const useProvetFocusManagement = ({
  sessionId,
  activeStep
}: UseProvetFocusManagementOptions): void => {
  // Track if we've completed initial setup to avoid focusing on first render
  const hasCompletedInitialSetup = useRef<boolean>(false);
  // Track the previous step to detect changes
  const previousStepRef = useRef<ProvetDialogStep | undefined>(undefined);

  useEffect(() => {
    // Skip focus on initial render
    if (!hasCompletedInitialSetup.current) {
      hasCompletedInitialSetup.current = true;
      previousStepRef.current = activeStep;
      return;
    }

    // Only apply focus if the step has actually changed
    if (activeStep === previousStepRef.current || activeStep === undefined) {
      return;
    }

    // Guard against invalid step values
    if (
      typeof activeStep !== "number" ||
      activeStep < 0 ||
      activeStep > ProvetDialogStep.Confirmed
    ) {
      console.warn("Invalid activeStep value:", activeStep);
      return;
    }

    previousStepRef.current = activeStep;

    // The contact details form focuses itself when it's shown and its step is open (see
    // ProvetContactDetails). Nothing is scheduled here that could fire later, after the
    // phone verification, while the customer types (observation 24).
    if (activeStep === ProvetDialogStep.ContactDetails) {
      return;
    }

    // Define the focus targets for each step
    const focusStep = () => {
      // Use requestAnimationFrame and setTimeout to ensure DOM is fully updated
      // This timing matches the scroll management hook
      requestAnimationFrame(() => {
        setTimeout(() => {
          let focusTarget: HTMLElement | null = null;

          try {
            switch (activeStep) {
              case ProvetDialogStep.SelectClinic:
                // Focus the search input in the department finder
                focusTarget = document.querySelector<HTMLElement>(
                  'input[name="department-search"]'
                );
                break;

              case ProvetDialogStep.SelectTreatment:
                // Focus the treatment search/filter input
                focusTarget = document.querySelector<HTMLElement>(
                  'input[name="treatment-search"]'
                );
                break;

              case ProvetDialogStep.SelectDateAndTime: {
                // Focus the veterinarian select button, or else the calendar's next month
                // button. Both go through the same guard as every other step.
                const isVetSelectShown = () => {
                  const vetWrapper = document.querySelector<HTMLElement>(
                    "#selected-veterinarian"
                  );
                  return !!vetWrapper && vetWrapper.offsetParent !== null;
                };
                const findVetOrCalendarButton = () => {
                  const vetButton = document.querySelector<HTMLElement>(
                    "#selected-veterinarian-button"
                  );
                  return isVetSelectShown() && vetButton?.offsetParent
                    ? vetButton
                    : document.querySelector<HTMLElement>("#calendar-next-month");
                };

                if (!isVetSelectShown()) {
                  // The veterinarian select isn't shown yet, retry after a delay
                  setTimeout(() => {
                    focusUnlessCustomerIsTyping(findVetOrCalendarButton());
                  }, 600);
                  return;
                }

                focusTarget = findVetOrCalendarButton();
                break;
              }

              case ProvetDialogStep.ImportantInformation:
              // Step 0 is never an active step: the flow starts at SelectClinic
              case ProvetDialogStep.Confirmation:
              case ProvetDialogStep.Confirmed:
                // No auto-focus for modal or confirmation view
                // These have their own focus management
                return;

              default:
                return;
            }
          } catch (error) {
            // Silently handle querySelector errors that can occur during rapid state changes
            console.warn("Focus management error:", error);
            return;
          }

          // Apply focus if we found a target
          focusUnlessCustomerIsTyping(focusTarget);
        }, 400); // Delay to run after scroll completes
      });
    };

    focusStep();
  }, [activeStep, sessionId]);
};
